/**
 * Shop by Category — infinite horizontal loop carousel
 *
 * Technique:
 * 1. DOM contains 3 identical sets of cards (prev + real + next).
 * 2. Initial scrollLeft sits at the start of the middle set.
 * 3. When scroll drifts into the first or third set, we silently
 *    jump by exactly one set-width so motion never visibly resets.
 * 4. Mouse drag updates scrollLeft only after a small movement threshold
 *    so normal clicks still open category links.
 * 5. Prev/next arrows scroll by one card width; drag/swipe still works.
 */
(function () {
  var SELECTOR = '[data-shop-by-category]';
  var DRAG_THRESHOLD = 8;
  var instances = new WeakMap();

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll ? scope.querySelectorAll(SELECTOR) : [];

    if (root && root.matches && root.matches(SELECTOR)) {
      initSection(root);
    }

    sections.forEach(initSection);
  }

  function initSection(section) {
    if (!section) return;
    if (instances.has(section)) destroySection(section);

    var viewport = section.querySelector('[data-shop-by-category-viewport]');
    var track = section.querySelector('[data-shop-by-category-track]');
    var sets = section.querySelectorAll('[data-shop-by-category-set]');
    var prevBtn = section.querySelector('[data-shop-by-category-prev]');
    var nextBtn = section.querySelector('[data-shop-by-category-next]');

    if (!viewport || !track || sets.length < 3) return;

    var state = {
      section: section,
      viewport: viewport,
      track: track,
      sets: sets,
      prevBtn: prevBtn,
      nextBtn: nextBtn,
      setWidth: 0,
      cardStep: 0,
      looping: false,
      pointerActive: false,
      dragging: false,
      startX: 0,
      startScroll: 0,
      suppressClick: false,
      onScroll: null,
      onPointerDown: null,
      onPointerMove: null,
      onPointerUp: null,
      onResize: null,
      onClickCapture: null,
      onPrev: null,
      onNext: null,
      resizeObserver: null,
    };

    function measure() {
      state.setWidth = sets[0].getBoundingClientRect().width;
      var card = sets[0].querySelector('.shop-by-category__card');
      if (card) {
        var styles = window.getComputedStyle(sets[0]);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        state.cardStep = card.getBoundingClientRect().width + gap;
      } else {
        state.cardStep = Math.max(state.setWidth / Math.max(sets[0].children.length, 1), 1);
      }
    }

    /* Silent teleport into the matching clone. Keep snap/momentum off
       briefly so mobile doesn't flash or stutter at the seam. */
    function applyLoopJump(targetLeft) {
      if (state.looping) return;
      if (Math.abs(viewport.scrollLeft - targetLeft) < 0.5) return;

      state.looping = true;
      viewport.classList.add('is-looping');

      var prevOverflow = viewport.style.overflowX;
      var prevBehavior = viewport.style.scrollBehavior;
      viewport.style.scrollBehavior = 'auto';
      /* Cancels iOS/Android momentum that fights the teleport */
      viewport.style.overflowX = 'hidden';
      viewport.scrollLeft = targetLeft;
      void viewport.offsetHeight;
      viewport.style.overflowX = prevOverflow || '';

      requestAnimationFrame(function () {
        viewport.scrollLeft = targetLeft;
        requestAnimationFrame(function () {
          viewport.style.scrollBehavior = prevBehavior || '';
          viewport.classList.remove('is-looping');
          state.looping = false;
        });
      });
    }

    function centerOnMiddleSet(force) {
      measure();
      if (!state.setWidth) return;
      var target = state.setWidth;
      if (force || Math.abs(viewport.scrollLeft - target) > 1) {
        applyLoopJump(target);
      }
    }

    function syncLoop() {
      if (state.looping) return;
      if (!state.setWidth) measure();
      if (!state.setWidth) return;

      var left = viewport.scrollLeft;
      /* Jump as soon as we leave the middle clone — waiting until 95%
         makes the seam visible (lag / flash on mobile). */
      if (left < state.setWidth) {
        applyLoopJump(left + state.setWidth);
        return;
      }
      if (left >= state.setWidth * 2) {
        applyLoopJump(left - state.setWidth);
      }
    }

    function scrollByCards(direction) {
      if (!state.cardStep) measure();
      if (!state.cardStep) return;
      viewport.scrollBy({
        left: direction * state.cardStep,
        behavior: 'smooth',
      });
    }

    function onScroll() {
      if (state.looping) return;
      syncLoop();
    }

    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      state.pointerActive = true;
      state.dragging = false;
      state.suppressClick = false;
      state.startX = event.clientX;
      state.startScroll = viewport.scrollLeft;
    }

    function onPointerMove(event) {
      if (!state.pointerActive) return;

      var dx = event.clientX - state.startX;

      // Only treat as drag after threshold — keeps clicks working
      if (!state.dragging && Math.abs(dx) < DRAG_THRESHOLD) return;

      if (!state.dragging) {
        state.dragging = true;
        state.suppressClick = true;
        viewport.classList.add('is-dragging');
        try {
          viewport.setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }

      viewport.scrollLeft = state.startScroll - dx;
      syncLoop();
    }

    function onPointerUp() {
      if (!state.pointerActive) return;

      state.pointerActive = false;
      state.dragging = false;
      viewport.classList.remove('is-dragging');

      // Clear suppressClick after this click event cycle finishes
      if (state.suppressClick) {
        window.setTimeout(function () {
          state.suppressClick = false;
        }, 0);
      }
    }

    function onClickCapture(event) {
      if (!state.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      state.suppressClick = false;
    }

    function onResize() {
      centerOnMiddleSet(true);
    }

    function onPrev(event) {
      event.preventDefault();
      event.stopPropagation();
      scrollByCards(-1);
    }

    function onNext(event) {
      event.preventDefault();
      event.stopPropagation();
      scrollByCards(1);
    }

    state.onScroll = onScroll;
    state.onPointerDown = onPointerDown;
    state.onPointerMove = onPointerMove;
    state.onPointerUp = onPointerUp;
    state.onClickCapture = onClickCapture;
    state.onResize = onResize;
    state.onPrev = onPrev;
    state.onNext = onNext;

    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    viewport.addEventListener('lostpointercapture', onPointerUp);
    viewport.addEventListener('click', onClickCapture, true);
    window.addEventListener('resize', onResize);

    if (prevBtn) prevBtn.addEventListener('click', onPrev);
    if (nextBtn) nextBtn.addEventListener('click', onNext);

    if (typeof ResizeObserver !== 'undefined') {
      state.resizeObserver = new ResizeObserver(function () {
        centerOnMiddleSet(true);
      });
      state.resizeObserver.observe(track);
    }

    requestAnimationFrame(function () {
      centerOnMiddleSet(true);
    });

    instances.set(section, state);
  }

  function destroySection(section) {
    var state = instances.get(section);
    if (!state) return;

    state.viewport.removeEventListener('scroll', state.onScroll);
    state.viewport.removeEventListener('pointerdown', state.onPointerDown);
    state.viewport.removeEventListener('pointermove', state.onPointerMove);
    state.viewport.removeEventListener('pointerup', state.onPointerUp);
    state.viewport.removeEventListener('pointercancel', state.onPointerUp);
    state.viewport.removeEventListener('lostpointercapture', state.onPointerUp);
    state.viewport.removeEventListener('click', state.onClickCapture, true);
    window.removeEventListener('resize', state.onResize);
    if (state.prevBtn && state.onPrev) state.prevBtn.removeEventListener('click', state.onPrev);
    if (state.nextBtn && state.onNext) state.nextBtn.removeEventListener('click', state.onNext);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    state.viewport.classList.remove('is-dragging');
    state.viewport.classList.remove('is-looping');
    instances.delete(section);
  }

  function onReady() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target) return;
    var section =
      event.target.matches && event.target.matches(SELECTOR)
        ? event.target
        : event.target.querySelector(SELECTOR);
    if (section) destroySection(section);
  });

  document.addEventListener('shopify:block:select', function (event) {
    if (!event.target) return;
    var section = event.target.closest(SELECTOR);
    if (!section) return;
    var state = instances.get(section);
    if (state) state.viewport.scrollLeft = state.setWidth || state.viewport.scrollLeft;
  });
})();
